import { getLetterhead, getFooter } from './pdfGenerator';

const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const RELATION_MAP = { W:'Wife', H:'Husband', S:'Son', D:'Daughter', A:'Account Holder', B:'Brother', F:'Father', M:'Mother' };

function fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-GB') : 'N/A'; }
function fmtAmt(v)  { return `&pound;${Number(v || 0).toFixed(2)}`; }

function calcAge(dob) {
  if (!dob) return 'N/A';
  const b = new Date(dob), t = new Date();
  let age = t.getFullYear() - b.getFullYear();
  if (t.getMonth() - b.getMonth() < 0 || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) age--;
  return age;
}

// payments = array from getSubscriptionPayments — {payment_month:'YYYY-MM-DD', amount, status, payment_method, paid_date}
// Normalise to { monthKey:'YYYY-MM', amount, status }
function normPayments(payments) {
  return (payments || []).map(p => ({
    monthKey: (p.payment_month || '').substring(0, 7),
    amount:   parseFloat(p.amount || 0),
    status:   p.status,
    method:   p.payment_method || '',
    paidDate: p.paid_date,
  }));
}

const thS = 'padding:6px 10px;font-weight:bold;font-size:11px;background:#1e293b;color:white;border:1px solid #334155;text-align:left;';
const tdS = 'padding:5px 10px;font-size:11px;border:1px solid #cbd5e1;';
const lblS = tdS + 'width:190px;font-weight:bold;background:#f8fafc;';
const tdR = tdS + 'text-align:right;';

// ──────────────────────────────────────────────────────────────────────────────
// 1. ACCOUNT OVERVIEW
// ──────────────────────────────────────────────────────────────────────────────
export function buildAccountOverviewHTML(member, family, payments) {
  const year = new Date().getFullYear();
  const curMonth = new Date().getMonth() + 1;
  const normed = normPayments(payments);
  const monthly = parseFloat(member.subscriptionAmount || member.contribution || 5);

  const yearNormed = normed.filter(p => p.monthKey.startsWith(String(year)) && p.status === 'paid');
  const totalPaidYear = yearNormed.reduce((s, p) => s + p.amount, 0);
  const allTimePaid   = normed.filter(p => p.status === 'paid').reduce((s, p) => s + p.amount, 0);

  let overdueAmt = 0, paidMonths = 0;
  const overdueList = [];
  for (let mo = 1; mo <= curMonth; mo++) {
    const key = `${year}-${String(mo).padStart(2,'0')}`;
    const paid = normed.filter(p => p.monthKey === key && p.status === 'paid').reduce((s, p) => s + p.amount, 0);
    if (paid >= monthly) paidMonths++;
    else { overdueAmt += (monthly - paid); overdueList.push(MONTHS_FULL[mo-1].substring(0,3)); }
  }

  const holderRows = [
    ['Full Name',            member.name || 'N/A'],
    ['Reference Number',     member.membershipRef || 'N/A'],
    ['Date of Birth',        member.date_of_birth ? `${fmtDate(member.date_of_birth)} (Age: ${calcAge(member.date_of_birth)})` : 'N/A'],
    ['Phone Number',         member.phone || 'N/A'],
    ['Email',                member.email || 'N/A'],
    ['Address',              [member.address, member.postcode].filter(Boolean).join(', ') || 'N/A'],
    ['Year Joined',          member.joinDate ? new Date(member.joinDate).getFullYear() : 'N/A'],
    ['Payment Method',       member.paymentMethod ? member.paymentMethod.replace(/_/g,' ') : 'N/A'],
    ['Monthly Contribution', fmtAmt(monthly)],
    ['Account Status',       member.status === 'active' ? '<span style="color:#16a34a;font-weight:bold;">Active</span>' : '<span style="color:#dc2626;font-weight:bold;">Inactive</span>'],
  ];

  let familyHTML = '';
  if (family.length > 0) {
    familyHTML = `
      <h3 style="font-size:13px;font-weight:bold;margin:16px 0 6px;border-bottom:1px solid #e2e8f0;padding-bottom:4px;">HOUSEHOLD MEMBERS (${family.length})</h3>
      <table style="width:100%;border-collapse:collapse;">
        <thead><tr>
          <th style="${thS}">#</th><th style="${thS}">Name</th><th style="${thS}">Relationship</th>
          <th style="${thS}">DOB / Age</th><th style="${thS}">Status</th>
        </tr></thead>
        <tbody>
          ${family.map((fm, i) => `<tr>
            <td style="${tdS}">${i+1}</td>
            <td style="${tdS}font-weight:600;">${fm.name}</td>
            <td style="${tdS}">${RELATION_MAP[fm.relation] || fm.relation}</td>
            <td style="${tdS}">${fm.date_of_birth ? `${fmtDate(fm.date_of_birth)} (${calcAge(fm.date_of_birth)})` : 'N/A'}</td>
            <td style="${tdS}"><span style="color:#16a34a;">Active</span></td>
          </tr>`).join('')}
        </tbody>
      </table>`;
  }

  const statsHTML = `
    <h3 style="font-size:13px;font-weight:bold;margin:16px 0 6px;border-bottom:1px solid #e2e8f0;padding-bottom:4px;">FINANCIAL SUMMARY &mdash; ${year}</h3>
    <table style="width:100%;border-collapse:collapse;">
      <tr>
        <td style="width:50%;vertical-align:top;padding-right:6px;">
          <table style="width:100%;border-collapse:collapse;">
            <tr><td style="${lblS}">Monthly Payment Plan</td><td style="${tdR}font-weight:bold;">${fmtAmt(monthly)}</td></tr>
            <tr><td style="${lblS}">Annual Expected (${year})</td><td style="${tdR}">${fmtAmt(monthly * 12)}</td></tr>
            <tr><td style="${lblS}">Total Paid (${year})</td><td style="${tdR}color:#16a34a;font-weight:bold;">${fmtAmt(totalPaidYear)}</td></tr>
            <tr><td style="${lblS}">Outstanding (${year})</td><td style="${tdR}color:${overdueAmt>0?'#dc2626':'#16a34a'};font-weight:bold;">${fmtAmt(overdueAmt)}</td></tr>
          </table>
        </td>
        <td style="width:50%;vertical-align:top;padding-left:6px;">
          <table style="width:100%;border-collapse:collapse;">
            <tr><td style="${lblS}">Months Paid (${year})</td><td style="${tdR}font-weight:bold;">${paidMonths} of ${curMonth}</td></tr>
            <tr><td style="${lblS}">Overdue Months</td><td style="${tdR}color:${overdueList.length>0?'#dc2626':'#16a34a'};font-weight:bold;">${overdueList.length > 0 ? overdueList.join(', ') : 'None'}</td></tr>
            <tr><td style="${lblS}">All-Time Paid (System)</td><td style="${tdR}font-weight:bold;">${fmtAmt(allTimePaid)}</td></tr>
          </table>
        </td>
      </tr>
    </table>`;

  return `
    ${getLetterhead()}
    <h2 style="font-size:16px;font-weight:bold;text-align:center;margin:0 0 3px;text-decoration:underline;">MEMBER ACCOUNT OVERVIEW</h2>
    <div style="font-size:11px;color:#666;text-align:center;margin-bottom:14px;">Generated: ${new Date().toLocaleDateString('en-GB')}</div>
    <h3 style="font-size:13px;font-weight:bold;margin-bottom:6px;border-bottom:1px solid #e2e8f0;padding-bottom:4px;">ACCOUNT HOLDER DETAILS</h3>
    <table style="width:100%;border-collapse:collapse;margin-bottom:5px;">
      ${holderRows.map(([l, v]) => `<tr><td style="${lblS}">${l}</td><td style="${tdS}">${v}</td></tr>`).join('')}
    </table>
    ${familyHTML}
    ${statsHTML}
    ${getFooter()}
  `;
}

// ──────────────────────────────────────────────────────────────────────────────
// 2. PAYMENT RECORD GRID (year matrix)
// ──────────────────────────────────────────────────────────────────────────────
export function buildPaymentReportHTML(member, payments) {
  const normed = normPayments(payments);
  const curYear = new Date().getFullYear();
  const curMonth = new Date().getMonth();
  const monthly = parseFloat(member.subscriptionAmount || member.contribution || 5);

  const years = [...new Set(normed.map(p => parseInt(p.monthKey.substring(0,4))))].sort();
  if (!years.length || !years.includes(curYear)) years.push(curYear);

  const cellS = 'padding:4px 2px;font-size:10px;text-align:center;border:1px solid #e2e8f0;min-width:32px;';
  const monthHeaders = MONTHS_FULL.map(m => `<th style="${thS}font-size:10px;padding:4px;text-align:center;">${m.substring(0,3)}</th>`).join('');

  const rows = years.map(yr => {
    const cells = MONTHS_FULL.map((_, mo) => {
      const key = `${yr}-${String(mo+1).padStart(2,'0')}`;
      const pay = normed.find(p => p.monthKey === key && p.status === 'paid');
      const isFuture = yr > curYear || (yr === curYear && mo > curMonth);
      if (isFuture) return `<td style="${cellS}background:#f8fafc;color:#94a3b8;">-</td>`;
      if (pay)      return `<td style="${cellS}background:#dcfce7;color:#166534;font-weight:bold;">${fmtAmt(pay.amount).replace('&pound;','£')}</td>`;
      return `<td style="${cellS}background:#fef2f2;color:#991b1b;font-weight:bold;">&times;</td>`;
    }).join('');
    const yearTotal = normed.filter(p => p.monthKey.startsWith(String(yr)) && p.status === 'paid').reduce((s, p) => s + p.amount, 0);
    return `<tr>
      <td style="${tdS}font-weight:bold;">${yr}</td>
      ${cells}
      <td style="${tdS}font-weight:bold;text-align:right;">${fmtAmt(yearTotal)}</td>
    </tr>`;
  }).join('');

  return `
    ${getLetterhead()}
    <h2 style="font-size:16px;font-weight:bold;text-align:center;margin:0 0 3px;text-decoration:underline;">PAYMENT RECORD GRID</h2>
    <div style="font-size:11px;color:#666;text-align:center;margin-bottom:6px;">
      ${member.name} &nbsp;&mdash;&nbsp; Ref: ${member.membershipRef} &nbsp;&mdash;&nbsp; Monthly: ${fmtAmt(monthly)} &nbsp;&mdash;&nbsp; Generated: ${new Date().toLocaleDateString('en-GB')}
    </div>
    <table style="width:100%;border-collapse:collapse;font-size:11px;">
      <thead><tr>
        <th style="${thS}padding:4px 6px;">Year</th>
        ${monthHeaders}
        <th style="${thS}font-size:10px;padding:4px;text-align:right;">Total</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div style="margin-top:10px;font-size:10px;color:#555;">
      <span style="background:#dcfce7;padding:2px 6px;margin-right:8px;border:1px solid #bbf7d0;">Paid</span>
      <span style="background:#fef2f2;padding:2px 6px;margin-right:8px;border:1px solid #fecaca;">&times; Missed/Overdue</span>
      <span style="background:#f8fafc;padding:2px 6px;border:1px solid #e2e8f0;">- Future</span>
    </div>
    ${getFooter()}
  `;
}

// ──────────────────────────────────────────────────────────────────────────────
// 3. ACCOUNT STATEMENT (date range)
// ──────────────────────────────────────────────────────────────────────────────
export function buildMemberStatementHTML(member, payments, dateFrom, dateTo) {
  const normed = normPayments(payments).filter(p => p.status === 'paid').sort((a,b) => a.monthKey.localeCompare(b.monthKey));
  const filtered = normed.filter(p => {
    if (dateFrom && p.monthKey < dateFrom.substring(0,7)) return false;
    if (dateTo   && p.monthKey > dateTo.substring(0,7))   return false;
    return true;
  });

  const totalPaid = filtered.reduce((s, p) => s + p.amount, 0);
  let running = 0;
  const rows = filtered.map(p => {
    running += p.amount;
    return `<tr>
      <td style="${tdS}">${p.paidDate ? fmtDate(p.paidDate) : '—'}</td>
      <td style="${tdS}">${p.monthKey}</td>
      <td style="${tdS}capitalize">${(p.method||'—').replace(/_/g,' ')}</td>
      <td style="${tdR}color:#16a34a;">${fmtAmt(p.amount)}</td>
      <td style="${tdR}font-weight:bold;">${fmtAmt(running)}</td>
    </tr>`;
  }).join('');

  const periodLabel = dateFrom || dateTo ? `${dateFrom || 'All'} to ${dateTo || 'Present'}` : 'All Time';

  return `
    ${getLetterhead()}
    <h2 style="font-size:16px;font-weight:bold;text-align:center;margin:0 0 3px;text-decoration:underline;">ACCOUNT STATEMENT</h2>
    <div style="font-size:11px;color:#666;text-align:center;margin-bottom:14px;">Period: ${periodLabel} &mdash; Generated: ${new Date().toLocaleDateString('en-GB')}</div>

    <table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
      <tr><td style="${lblS}">Name</td><td style="${tdS}">${member.name}</td></tr>
      <tr><td style="${lblS}">Reference</td><td style="${tdS}">${member.membershipRef}</td></tr>
      <tr><td style="${lblS}">Address</td><td style="${tdS}">${[member.address, member.postcode].filter(Boolean).join(', ') || 'N/A'}</td></tr>
    </table>

    <table style="width:100%;border-collapse:collapse;margin-bottom:14px;">
      <tr><td style="${lblS}">Total Paid (Period)</td><td style="${tdR}font-weight:bold;font-size:14px;color:#16a34a;">${fmtAmt(totalPaid)}</td></tr>
      <tr><td style="${lblS}">Number of Payments</td><td style="${tdR}font-weight:bold;">${filtered.length}</td></tr>
    </table>

    <table style="width:100%;border-collapse:collapse;">
      <thead><tr>
        <th style="${thS}">Paid Date</th><th style="${thS}">Period</th><th style="${thS}">Method</th>
        <th style="${thS}text-align:right;">Amount</th><th style="${thS}text-align:right;">Running Total</th>
      </tr></thead>
      <tbody>${rows || `<tr><td colspan="5" style="${tdS}text-align:center;color:#94a3b8;">No payments in this period</td></tr>`}</tbody>
    </table>
    ${getFooter()}
  `;
}

// ──────────────────────────────────────────────────────────────────────────────
// 4. ANNUAL SUMMARY
// ──────────────────────────────────────────────────────────────────────────────
export function buildAnnualSummaryHTML(member, payments, year) {
  const normed = normPayments(payments);
  const monthly = parseFloat(member.subscriptionAmount || member.contribution || 5);
  const curYear = new Date().getFullYear();
  const curMonth = new Date().getMonth();

  let totalExpected = 0, totalPaid = 0;
  const rows = MONTHS_FULL.map((name, mo) => {
    const key = `${year}-${String(mo+1).padStart(2,'0')}`;
    const isFuture = parseInt(year) > curYear || (parseInt(year) === curYear && mo > curMonth);
    const paid = normed.filter(p => p.monthKey === key && p.status === 'paid').reduce((s,p) => s + p.amount, 0);
    const balance = paid - monthly;

    let statusLabel, statusStyle;
    if (isFuture) { statusLabel = 'Upcoming'; statusStyle = 'color:#94a3b8;'; }
    else if (paid >= monthly) { statusLabel = 'Paid'; statusStyle = 'color:#16a34a;font-weight:bold;'; }
    else if (paid > 0) { statusLabel = 'Underpaid'; statusStyle = 'color:#d97706;font-weight:bold;'; }
    else { statusLabel = 'Unpaid'; statusStyle = 'color:#dc2626;font-weight:bold;'; }

    if (!isFuture) { totalExpected += monthly; totalPaid += paid; }

    return `<tr>
      <td style="${tdS}">${name}</td>
      <td style="${tdR}">${isFuture ? '—' : fmtAmt(monthly)}</td>
      <td style="${tdR}${paid > 0 ? 'color:#16a34a;' : ''}">${isFuture ? '—' : fmtAmt(paid)}</td>
      <td style="${tdR}${balance >= 0 ? 'color:#16a34a;' : 'color:#dc2626;'}">${isFuture ? '—' : fmtAmt(balance)}</td>
      <td style="${tdS}${statusStyle}">${statusLabel}</td>
    </tr>`;
  }).join('');

  return `
    ${getLetterhead()}
    <h2 style="font-size:16px;font-weight:bold;text-align:center;margin:0 0 3px;text-decoration:underline;">ANNUAL CONTRIBUTION SUMMARY &mdash; ${year}</h2>
    <div style="font-size:11px;color:#666;text-align:center;margin-bottom:14px;">${member.name} &mdash; Ref: ${member.membershipRef} &mdash; Generated: ${new Date().toLocaleDateString('en-GB')}</div>
    <table style="width:100%;border-collapse:collapse;">
      <thead><tr>
        <th style="${thS}">Month</th><th style="${thS}text-align:right;">Expected</th>
        <th style="${thS}text-align:right;">Paid</th><th style="${thS}text-align:right;">Balance</th>
        <th style="${thS}">Status</th>
      </tr></thead>
      <tbody>
        ${rows}
        <tr style="background:#f1f5f9;">
          <td style="${tdS}font-weight:bold;">TOTAL</td>
          <td style="${tdR}font-weight:bold;">${fmtAmt(totalExpected)}</td>
          <td style="${tdR}font-weight:bold;color:#16a34a;">${fmtAmt(totalPaid)}</td>
          <td style="${tdR}font-weight:bold;color:${totalPaid>=totalExpected?'#16a34a':'#dc2626'};">${fmtAmt(totalPaid-totalExpected)}</td>
          <td style="${tdS}"></td>
        </tr>
      </tbody>
    </table>
    ${getFooter()}
  `;
}

// ──────────────────────────────────────────────────────────────────────────────
// 5. OVERDUE LETTER
// ──────────────────────────────────────────────────────────────────────────────
export function buildOverdueLetterHTML(member, family, payments, fromMonth, dateTo) {
  const normed = normPayments(payments);
  const monthly = parseFloat(member.subscriptionAmount || member.contribution || 5);
  const curYear  = new Date().getFullYear();
  const curMonth = new Date().getMonth() + 1;

  let [sy, sm] = fromMonth ? fromMonth.split('-').map(Number) : [curYear, 1];
  let [ey, em] = dateTo    ? dateTo.split('-').map(Number)    : [curYear, curMonth];

  const overdueMonths = [];
  let y = sy, m = sm;
  while (y < ey || (y === ey && m <= em)) {
    const key = `${y}-${String(m).padStart(2,'0')}`;
    const paid = normed.filter(p => p.monthKey === key && p.status === 'paid').reduce((s, p) => s + p.amount, 0);
    if (paid < monthly) {
      overdueMonths.push({ month: `${MONTHS_FULL[m-1]} ${y}`, expected: monthly, paid, outstanding: monthly - paid });
    }
    m++; if (m > 12) { m = 1; y++; }
  }

  const totalOverdue = overdueMonths.reduce((s, mo) => s + mo.outstanding, 0);
  const addr = [member.name, member.address, member.postcode].filter(Boolean);

  const tableRows = overdueMonths.map(mo => `<tr>
    <td style="${tdS}">${mo.month}</td>
    <td style="${tdR}">${fmtAmt(mo.expected)}</td>
    <td style="${tdR}">${fmtAmt(mo.paid)}</td>
    <td style="${tdR}font-weight:bold;color:#dc2626;">${fmtAmt(mo.outstanding)}</td>
  </tr>`).join('');

  const householdParts = [`${member.name} (Account Holder): ${fmtAmt(monthly)}`];
  family.forEach(fm => householdParts.push(`${fm.name} (${RELATION_MAP[fm.relation]||fm.relation}): &pound;5.00`));

  const html = `
    ${getLetterhead()}
    <div style="text-align:right;font-size:12px;margin-bottom:15px;">${new Date().toLocaleDateString('en-GB')}</div>
    <div style="font-size:13px;line-height:1.8;margin-bottom:8px;">${addr.map(l=>`<div>${l}</div>`).join('')}</div>
    <div style="font-size:12px;margin-bottom:4px;"><strong>Ref:</strong> ${member.membershipRef}</div>
    <div style="font-size:15px;font-weight:bold;margin-bottom:12px;text-decoration:underline;">RE: Outstanding Contribution Payment</div>
    <div style="font-size:12px;line-height:1.8;">
      <p style="margin-bottom:10px;">Dear Member,</p>
      <p style="margin-bottom:10px;">We are writing to inform you that our records indicate outstanding contribution payments on your account. As a valued member of Al-Iman Society, your contributions play a vital role in maintaining services and supporting our community.</p>
      ${overdueMonths.length > 0 ? `
      <table style="width:100%;border-collapse:collapse;margin:10px 0 12px;">
        <thead><tr>
          <th style="${thS}">Month</th><th style="${thS}text-align:right;">Expected</th>
          <th style="${thS}text-align:right;">Paid</th><th style="${thS}text-align:right;">Outstanding</th>
        </tr></thead>
        <tbody>
          ${tableRows}
          <tr style="background:#fef2f2;">
            <td colspan="3" style="${tdS}font-weight:bold;text-align:right;font-size:13px;">Total Outstanding:</td>
            <td style="${tdR}font-weight:bold;font-size:14px;color:#dc2626;">${fmtAmt(totalOverdue)}</td>
          </tr>
        </tbody>
      </table>` : '<p style="color:#16a34a;font-weight:bold;margin:10px 0;">No outstanding payments found for this period.</p>'}
      <p style="margin-bottom:8px;">Your monthly contribution of <strong>${fmtAmt(monthly)}</strong> covers:</p>
      <ul style="margin:0 0 10px 20px;list-style:disc;">${householdParts.map(p=>`<li>${p}</li>`).join('')}</ul>
      <p style="margin-bottom:8px;">We kindly request that you settle the outstanding amount at your earliest convenience. Payments can be made via bank transfer, cash, or by visiting the Islamic Centre.</p>
      <p style="margin-bottom:8px;">JazakAllah Khair for your continued support.</p>
      <p style="margin-top:20px;">Yours sincerely,</p>
      <p style="font-weight:bold;font-size:13px;margin-top:14px;">Al-Iman Society of Northamptonshire</p>
    </div>
    ${getFooter()}
  `;
  return { html, overdueData: { totalOverdue, overdueMonths } };
}

// ──────────────────────────────────────────────────────────────────────────────
// 6. WELCOME LETTER
// ──────────────────────────────────────────────────────────────────────────────
export function buildWelcomeLetterHTML(member, family) {
  const monthly = parseFloat(member.subscriptionAmount || member.contribution || 5);
  const addr = [member.name, member.address, member.postcode].filter(Boolean);
  const householdParts = [`${member.name} (Account Holder): ${fmtAmt(monthly)}`];
  family.forEach(fm => householdParts.push(`${fm.name} (${RELATION_MAP[fm.relation]||fm.relation}): &pound;5.00`));

  return `
    ${getLetterhead()}
    <div style="text-align:right;font-size:12px;margin-bottom:15px;">${new Date().toLocaleDateString('en-GB')}</div>
    <div style="font-size:13px;line-height:1.8;margin-bottom:8px;">${addr.map(l=>`<div>${l}</div>`).join('')}</div>
    <div style="font-size:12px;margin-bottom:4px;"><strong>Ref:</strong> ${member.membershipRef}</div>
    <div style="font-size:15px;font-weight:bold;margin-bottom:12px;text-decoration:underline;">Welcome to Al-Iman Bereavement Fund</div>
    <div style="font-size:12px;line-height:1.8;">
      <p style="margin-bottom:10px;">Dear ${member.name},</p>
      <p style="margin-bottom:10px;">Assalamu Alaikum. On behalf of Al-Iman Society of Northamptonshire, we are pleased to welcome you as a member of our Bereavement Fund.</p>
      <p style="margin-bottom:10px;">Your membership details are as follows:</p>
      <table style="width:100%;border-collapse:collapse;margin:8px 0 12px;">
        <tr><td style="${lblS}">Reference Number</td><td style="${tdS}">${member.membershipRef}</td></tr>
        <tr><td style="${lblS}">Account Holder</td><td style="${tdS}">${member.name}</td></tr>
        <tr><td style="${lblS}">Monthly Contribution</td><td style="${tdS}font-weight:bold;">${fmtAmt(monthly)}</td></tr>
      </table>
      <p style="margin-bottom:6px;"><strong>Household breakdown:</strong></p>
      <ul style="margin:0 0 10px 20px;list-style:disc;">${householdParts.map(p=>`<li>${p}</li>`).join('')}</ul>
      <p style="margin-bottom:10px;">The Bereavement Fund provides financial support to families during their time of bereavement. Contributions are collected monthly and are used to assist with funeral costs and related expenses.</p>
      <p style="margin-bottom:10px;">Payments can be made via bank transfer, cash, or by visiting the Islamic Centre.</p>
      <p style="margin-bottom:8px;">JazakAllah Khair.</p>
      <p style="margin-top:20px;">Yours sincerely,</p>
      <p style="font-weight:bold;font-size:13px;margin-top:14px;">Al-Iman Society of Northamptonshire</p>
    </div>
    ${getFooter()}
  `;
}
